import { useUserStore } from '@/stores/userStore';
import { cameraControls, render, scene } from '@/threejs/core';
import * as THREE from 'three';
import { ref } from 'vue';
export const useWebsockets = () => {
  const userStore = useUserStore();

  const userCamera = ref([]);
  const userAvatarRef = ref({});
  let lastWhisperTime = 0;
  const WHISPER_THROTTLE_MS = 100;

  const createUserCameraHelper = (user) => {
    // Only create sprite for other users, not the current user
    if (user.id !== userStore.user.id) {
      if (!userCamera.value.find((u) => u.id === user.id)) {
        userCamera.value.push(user);
        userAvatarRef.value[user.id] = [0, 0, 0];

        // Create a sprite with user's avatar image
        const spriteMaterial = new THREE.SpriteMaterial({
          map: new THREE.TextureLoader().load(user.photo_url || '/default-avatar.png'),
          transparent: true,
          opacity: 0.8,
        });
        const userSprite = new THREE.Sprite(spriteMaterial);
        userSprite.scale.set(0.5, 0.5, 1); // Make it a reasonable size

        // Create a text label with user's name
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 256;
        canvas.height = 64;

        // Set background with rounded corners
        context.fillStyle = 'rgba(0, 0, 0, 0.4)';
        const radius = 8; // Corner radius
        const width = canvas.width;
        const height = canvas.height;

        // Draw rounded rectangle
        context.beginPath();
        context.moveTo(radius, 0);
        context.lineTo(width - radius, 0);
        context.quadraticCurveTo(width, 0, width, radius);
        context.lineTo(width, height - radius);
        context.quadraticCurveTo(width, height, width - radius, height);
        context.lineTo(radius, height);
        context.quadraticCurveTo(0, height, 0, height - radius);
        context.lineTo(0, radius);
        context.quadraticCurveTo(0, 0, radius, 0);
        context.closePath();
        context.fill();

        // Set text style
        context.fillStyle = '#FFFFFF';
        context.font = '24px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';

        // Draw text
        context.fillText(user.name || 'Unknown User', canvas.width / 2, canvas.height / 2);

        // Create texture from canvas
        const textTexture = new THREE.CanvasTexture(canvas);
        const textMaterial = new THREE.SpriteMaterial({
          map: textTexture,
          transparent: true,
          opacity: 0.9,
        });
        const nameLabel = new THREE.Sprite(textMaterial);
        nameLabel.scale.set(2, 0.5, 1); // Wider for text
        nameLabel.position.y = 0.8; // Position above the avatar

        // Create a dashed line from sprite to target
        const lineGeometry = new THREE.BufferGeometry();
        const lineMaterial = new THREE.LineDashedMaterial({
          color: 0xff6df0,
          dashSize: 0.1,
          gapSize: 0.2,
          transparent: true,
          opacity: 0.6,
        });

        // Initial line points (will be updated when camera position is received)
        const linePoints = [
          new THREE.Vector3(0, 0, 0), // Start at sprite position
          new THREE.Vector3(0, 0, -1), // End 1 unit away in negative Z direction
        ];
        lineGeometry.setFromPoints(linePoints);
        lineGeometry.computeBoundingSphere();

        const userLine = new THREE.Line(lineGeometry, lineMaterial);
        userLine.computeLineDistances(); // Required for dashed lines

        // Set user data to identify this line
        userLine.userData = {
          userId: user.id,
          userName: user.name,
          type: 'userAvatarLine',
          selectable: false,
        };

        // Set user data to identify this sprite
        userSprite.userData = {
          userId: user.id,
          userName: user.name,
          type: 'userAvatar',
          selectable: false,
        };

        // Set user data to identify this name label
        nameLabel.userData = {
          userId: user.id,
          userName: user.name,
          type: 'userNameLabel',
          selectable: false,
        };

        // Position the sprite at a default location (will be updated when camera position is received)
        userSprite.position.set(0, 0, 0);
        nameLabel.position.set(0, 0.8, 0); // Position above the sprite

        // Add sprite, name label, and line to scene
        scene.add(userSprite);
        scene.add(nameLabel);
        scene.add(userLine);

        // Store reference to sprite, name label, and line for later updates
        userAvatarRef.value[user.id] = {
          sprite: userSprite,
          nameLabel: nameLabel,
          line: userLine,
        };

        // Trigger render to show the new sprite and line
        render();
      }
    }
  };

  const removeUserCameraHelper = (user) => {
    if (userCamera.value.find((u) => u.id === user.id)) {
      userCamera.value = userCamera.value.filter((u) => u.id !== user.id);

      // Remove the sprite and line meshes from the scene
      const userAvatar = userAvatarRef.value[user.id];

      if (userAvatar) {
        // Remove sprite
        if (userAvatar.sprite && userAvatar.sprite.parent) {
          console.log('Removing sprite for user:', user.id, userAvatar.sprite);
          userAvatar.sprite.parent.remove(userAvatar.sprite);

          // Dispose of geometry and material
          if (userAvatar.sprite.material) {
            userAvatar.sprite.material.dispose();
          }
        }

        // Remove name label
        if (userAvatar.nameLabel && userAvatar.nameLabel.parent) {
          console.log('Removing name label for user:', user.id, userAvatar.nameLabel);
          userAvatar.nameLabel.parent.remove(userAvatar.nameLabel);

          // Dispose of geometry and material
          if (userAvatar.nameLabel.material) {
            userAvatar.nameLabel.material.dispose();
          }
        }

        // Remove line
        if (userAvatar.line && userAvatar.line.parent) {
          console.log('Removing line for user:', user.id, userAvatar.line);
          userAvatar.line.parent.remove(userAvatar.line);

          // Dispose of geometry and material
          if (userAvatar.line.geometry) {
            userAvatar.line.geometry.dispose();
          }
          if (userAvatar.line.material) {
            userAvatar.line.material.dispose();
          }
        }
      }

      delete userAvatarRef.value[user.id];

      // Trigger render to update the scene
      render();
    }
  };

  const sendInitialCameraPosition = () => {
    //NOTE: replace  sceneStore.currentScene.uuid with null as part of the cleanup process. If needed may be we need replace it with anything and reuse the function later
    const currentSceneUuid = null; //sceneStore.currentScene.uuid

    if (window.Echo && currentSceneUuid && cameraControls) {
      const target = new THREE.Vector3();
      cameraControls.getTarget(target);
      window.Echo.private('asset.' + currentSceneUuid).whisper('movingCamera', {
        user: userStore.user.id,
        camera: {
          x: cameraControls.camera.position.x,
          y: cameraControls.camera.position.y,
          z: cameraControls.camera.position.z,
        },
        target: {
          x: target.x,
          y: target.y,
          z: target.z,
        },
      });
    }
  };

  const sendCameraPosition = () => {
    const currentSceneUuid = null; //sceneStore.currentScene.uuid
    // NOTE: need to update the logic where if we considering using it later
    const now = Date.now();
    if (window.Echo && currentSceneUuid && now - lastWhisperTime > WHISPER_THROTTLE_MS && cameraControls) {
      const target = new THREE.Vector3();
      cameraControls.getTarget(target);

      window.Echo.private('asset.' + currentSceneUuid).whisper('movingCamera', {
        user: userStore.user.id,
        camera: {
          x: cameraControls.camera.position.x,
          y: cameraControls.camera.position.y,
          z: cameraControls.camera.position.z,
        },
        target: {
          x: target.x,
          y: target.y,
          z: target.z,
        },
      });

      lastWhisperTime = now;
    }
  };

  const connectWebsockets = async () => {
    const currentSceneUuid = null; //sceneStore.currentScene.uuid
    // NOTE: need to update the logic where if we considering using it later
    if (!window.Echo || !currentSceneUuid) {
      console.warn('Echo or scene not available for WebSocket connection');
      return;
    }

    const channelName = 'asset.' + currentSceneUuid;

    window.Echo.join(channelName)
      .here((users) => {
        users.forEach((user) => {
          createUserCameraHelper(user);
        });
        // Send initial camera position to other users
        setTimeout(() => {
          sendInitialCameraPosition();
        }, 1000); // Small delay to ensure other users have joined
      })
      .joining((user) => {
        createUserCameraHelper(user);
      })
      .leaving((user) => {
        removeUserCameraHelper(user);
      });

    window.Echo.private('asset.' + currentSceneUuid)

      .listen('.AssetSettingsUpdated', (e) => {})
      .listen('.AssetVariantSequenceUpdated', (e) => {})
      .listenForWhisper('movingCamera', (e) => {
        const user = userCamera.value.find((u) => u.id === e.user);
        if (user) {
          user.camera = e.camera;
          user.target = e.target;

          // Update the sprite position for this user
          const userAvatar = userAvatarRef.value[e.user];
          if (userAvatar && userAvatar.sprite && userAvatar.sprite.userData.type === 'userAvatar') {
            // Update sprite position to camera position
            userAvatar.sprite.position.set(e.camera.x, e.camera.y, e.camera.z);

            // Update name label position (keep it above the sprite)
            userAvatar.nameLabel.position.set(e.camera.x, e.camera.y + 0.8, e.camera.z);

            // Update line to point from camera position to target
            if (userAvatar.line) {
              const linePoints = [
                new THREE.Vector3(e.camera.x, e.camera.y, e.camera.z), // Start at camera position
                new THREE.Vector3(e.target.x, e.target.y, e.target.z), // End at target
              ];

              // Update line geometry
              userAvatar.line.geometry.setFromPoints(linePoints);
              userAvatar.line.geometry.computeBoundingSphere();
              userAvatar.line.computeLineDistances(); // Required for dashed lines
            }

            // Trigger render to update the scene
            render();
          }

          if (userStore.followingUser === e.user) {
            if (cameraControls) {
              cameraControls.setPosition(e.camera.x, e.camera.y, e.camera.z, true);
              cameraControls.setTarget(e.target.x, e.target.y, e.target.z, true);
            }
          }
        } else {
          console.log('User not found in userCamera array:', e.user);
        }
      })
      .listenForWhisper('requestCameraPosition', (e) => {
        // Only respond if this request is for the current user
        if (e.targetUser === userStore.user.id && cameraControls) {
          const target = new THREE.Vector3();
          cameraControls.getTarget(target);

          window.Echo.private('asset.' + currentSceneUuid).whisper('movingCamera', {
            user: userStore.user.id,
            camera: {
              x: cameraControls.camera.position.x,
              y: cameraControls.camera.position.y,
              z: cameraControls.camera.position.z,
            },
            target: {
              x: target.x,
              y: target.y,
              z: target.z,
            },
          });
        }
      });
  };

  const disconnectWebsockets = () => {
    const currentSceneUuid = null; //sceneStore.currentScene.uuid
    if (window.Echo && currentSceneUuid) {
      const channelName = 'asset.' + currentSceneUuid;
      window.Echo.leave(channelName);
    }
  };

  const cleanupUserSprites = () => {
    Object.keys(userAvatarRef.value).forEach((userId) => {
      const userAvatar = userAvatarRef.value[userId];
      if (userAvatar) {
        // Remove sprite
        if (userAvatar.sprite && userAvatar.sprite.parent) {
          console.log('Cleanup: Removing sprite for user:', userId);
          userAvatar.sprite.parent.remove(userAvatar.sprite);

          // Dispose of geometry and material
          if (userAvatar.sprite.material) {
            userAvatar.sprite.material.dispose();
          }
        }

        // Remove name label
        if (userAvatar.nameLabel && userAvatar.nameLabel.parent) {
          console.log('Cleanup: Removing name label for user:', userId);
          userAvatar.nameLabel.parent.remove(userAvatar.nameLabel);

          // Dispose of geometry and material
          if (userAvatar.nameLabel.material) {
            userAvatar.nameLabel.material.dispose();
          }
        }

        // Remove line
        if (userAvatar.line && userAvatar.line.parent) {
          console.log('Cleanup: Removing line for user:', userId);
          userAvatar.line.parent.remove(userAvatar.line);

          // Dispose of geometry and material
          if (userAvatar.line.geometry) {
            userAvatar.line.geometry.dispose();
          }
          if (userAvatar.line.material) {
            userAvatar.line.material.dispose();
          }
        }
      }
    });

    // Clear the references
    userAvatarRef.value = {};
    userCamera.value = [];
    lastWhisperTime = 0;
  };

  const cleanup = () => {
    disconnectWebsockets();
    cleanupUserSprites();
  };

  return {
    userCamera,
    userAvatarRef,
    connectWebsockets,
    disconnectWebsockets,
    sendCameraPosition,
    sendInitialCameraPosition,
    cleanupUserSprites,
    cleanup,
  };
};
